import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import { formatPrice } from '../utils/formatters';
import { calculateAIValuation } from '../utils/domainValuation';

const FeaturesPage = () => {
  const navigate = useNavigate();
  const [testDomain, setTestDomain] = useState('');
  const [valuation, setValuation] = useState(null);
  const [activeTab, setActiveTab] = useState('buyNow');

  const analyzeDemo = () => {
    if (testDomain) {
      const result = calculateAIValuation(testDomain);
      setValuation(result);
    }
  };

  const tradingMethods = [
    { id: 'buyNow', label: 'Buy Now' },
    { id: 'offers', label: 'Make Offer' },
    { id: 'auction', label: 'Auction' },
    { id: 'trade', label: 'Domain Swap' }
  ];

  return (
    <div className="min-h-screen py-20">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-20">
          <h1 className="text-5xl font-bold mb-6">Platform Features</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Everything you need to succeed in domain trading, powered by cutting-edge technology
          </p>
        </div>

        {/* Feature 1: Instant Trading */}
        <section className="mb-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge variant="primary" className="mb-4">Core Feature</Badge>
              <h2 className="text-4xl font-bold mb-6">Instant Trading</h2>
              <p className="text-lg text-gray-600 mb-6">
                List domains in seconds. Buy with one click. No waiting days for
                transfers or approvals. Our escrow system makes trading instant and secure.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-green-500 text-xl">✓</span>
                  <span>One-click purchases</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 text-xl">✓</span>
                  <span>Instant listing updates</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 text-xl">✓</span>
                  <span>Secure escrow system</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 text-xl">✓</span>
                  <span>Real-time notifications</span>
                </li>
              </ul>
            </div>
            <div>
              <Card className="p-8 bg-gradient-to-br from-blue-50 to-purple-50">
                <div className="text-center">
                  <div className="text-6xl mb-4">⚡</div>
                  <p className="text-2xl font-bold mb-2">3 seconds</p>
                  <p className="text-gray-600">Average transaction time</p>
                  <div className="mt-8 grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="font-bold text-2xl text-blue-600">10K+</p>
                      <p className="text-gray-600">Daily Trades</p>
                    </div>
                    <div>
                      <p className="font-bold text-2xl text-blue-600">99.9%</p>
                      <p className="text-gray-600">Uptime</p>
                    </div>
                    <div>
                      <p className="font-bold text-2xl text-blue-600">$0</p>
                      <p className="text-gray-600">Setup Fee</p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* Feature 2: AI-Powered Valuations */}
        <section className="mb-32 bg-gray-50 -mx-4 px-4 py-20">
          <div className="container mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <Card className="p-6">
                  <h3 className="font-bold mb-4">Try It Yourself</h3>
                  <input
                    type="text"
                    placeholder="Enter a domain name..."
                    className="w-full px-4 py-3 border rounded-lg mb-4"
                    value={testDomain}
                    onChange={(e) => setTestDomain(e.target.value)}
                  />
                  <Button fullWidth onClick={analyzeDemo}>
                    Analyze Domain
                  </Button>

                  {valuation && (
                    <div className="mt-6 space-y-4">
                      <div>
                        <p className="text-sm text-gray-600">Estimated Value</p>
                        <p className="text-3xl font-bold text-blue-600">
                          {formatPrice(valuation.min)} - {formatPrice(valuation.max)}
                        </p>
                        <p className="text-sm text-gray-600">
                          Confidence: {valuation.confidence}%
                        </p>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">Length Score</span>
                          <div className="flex gap-1">
                            {[...Array(5)].map((_, i) => (
                              <div
                                key={i}
                                className={`w-4 h-4 rounded ${
                                  i < valuation.breakdown.length ? 'bg-blue-600' : 'bg-gray-200'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm">Brandability</span>
                          <div className="flex gap-1">
                            {[...Array(5)].map((_, i) => (
                              <div
                                key={i}
                                className={`w-4 h-4 rounded ${
                                  i < valuation.breakdown.brandability ? 'bg-blue-600' : 'bg-gray-200'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm">TLD Premium</span>
                          <div className="flex gap-1">
                            {[...Array(5)].map((_, i) => (
                              <div
                                key={i}
                                className={`w-4 h-4 rounded ${
                                  i < valuation.breakdown.tldScore ? 'bg-blue-600' : 'bg-gray-200'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              </div>

              <div className="order-1 lg:order-2">
                <Badge variant="info" className="mb-4">AI Technology</Badge>
                <h2 className="text-4xl font-bold mb-6">AI-Powered Valuations</h2>
                <p className="text-lg text-gray-600 mb-6">
                  Our proprietary algorithm analyzes dozens of factors to give you
                  accurate domain valuations instantly. No more guessing.
                </p>
                <p className="text-gray-600">
                  Factors analyzed: Length, TLD premium, keyword value, brandability,
                  memorability, and market trends.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Feature 3: Multiple Trading Methods */}
        <section className="mb-32">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Multiple Trading Methods
            </h2>
            <p className="text-gray-600">
              Choose the method that works best for you
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="flex gap-2 mb-8 border-b overflow-x-auto">
              {tradingMethods.map(method => (
                <button
                  key={method.id}
                  onClick={() => setActiveTab(method.id)}
                  className={`px-6 py-3 font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === method.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {method.label}
                </button>
              ))}
            </div>

            <Card className="p-8">
              {activeTab === 'buyNow' && (
                <div>
                  <h3 className="text-2xl font-bold mb-4">Buy Now</h3>
                  <p className="text-gray-600 mb-6">
                    Fixed price, instant purchase. The fastest way to acquire a domain.
                  </p>
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <p className="text-sm text-gray-600 mb-2">Example Transaction:</p>
                    <p className="font-bold text-lg">TechStartup.io</p>
                    <p className="text-3xl font-bold text-blue-600 my-3">{formatPrice(5000)}</p>
                    <Button fullWidth>Buy Now</Button>
                  </div>
                </div>
              )}
              {activeTab === 'offers' && (
                <div>
                  <h3 className="text-2xl font-bold mb-4">Make an Offer</h3>
                  <p className="text-gray-600 mb-6">
                    Negotiate the price. Sellers can accept, counter, or decline your offer.
                  </p>
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <p className="text-sm text-gray-600 mb-2">Listed at: {formatPrice(5000)}</p>
                    <input
                      type="number"
                      placeholder="Enter your offer..."
                      className="w-full px-4 py-3 border rounded-lg mb-3"
                    />
                    <Button fullWidth>Submit Offer</Button>
                  </div>
                </div>
              )}
              {activeTab === 'auction' && (
                <div>
                  <h3 className="text-2xl font-bold mb-4">Auction Bidding</h3>
                  <p className="text-gray-600 mb-6">
                    Compete with other buyers. Highest bid wins when auction ends.
                  </p>
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <div className="flex justify-between mb-4">
                      <div>
                        <p className="text-sm text-gray-600">Current Bid</p>
                        <p className="text-2xl font-bold text-blue-600">{formatPrice(4800)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Ends In</p>
                        <p className="text-2xl font-bold">2d 14h</p>
                      </div>
                    </div>
                    <Button fullWidth>Place Bid</Button>
                  </div>
                </div>
              )}
              {activeTab === 'trade' && (
                <div>
                  <h3 className="text-2xl font-bold mb-4">Domain-for-Domain Trade</h3>
                  <p className="text-gray-600 mb-6">
                    Swap your domain for theirs. No cash needed. Perfect for strategic trades.
                  </p>
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <p className="text-sm text-gray-600 mb-2">They want:</p>
                    <p className="font-bold mb-4">Similar value .com domains, Tech-related</p>
                    <select className="w-full px-4 py-3 border rounded-lg mb-3">
                      <option>Select your domain to trade...</option>
                      <option>MyStartup.com ({formatPrice(5200)})</option>
                      <option>TechVenture.io ({formatPrice(4800)})</option>
                    </select>
                    <Button fullWidth>Propose Trade</Button>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </section>

        {/* Feature 4: Social Trading */}
        <section className="mb-32 bg-gray-50 -mx-4 px-4 py-20">
          <div className="container mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <Badge variant="warning" className="mb-4">Social Features</Badge>
                <h2 className="text-4xl font-bold mb-6">Social Discovery</h2>
                <p className="text-lg text-gray-600 mb-6">
                  Follow top traders, discover trending domains, comment on listings,
                  and build your reputation in the community.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="text-green-500 text-xl">✓</span>
                    <span>Follow your favorite traders</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-500 text-xl">✓</span>
                    <span>Comment and engage on listings</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-500 text-xl">✓</span>
                    <span>Get personalized recommendations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-500 text-xl">✓</span>
                    <span>Build your trader reputation</span>
                  </li>
                </ul>
              </div>
              <Card className="p-8">
                <div className="text-center mb-6">
                  <div className="text-5xl mb-4">💬</div>
                  <p className="text-2xl font-bold">10K+ Active</p>
                  <p className="text-gray-600">Community Members</p>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="text-2xl">🔥</span>
                    <p><strong>@DomainKing</strong> just listed CryptoHub.io</p>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="text-2xl">💬</span>
                    <p><strong>@TechPro</strong> commented on AINews.com</p>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="text-2xl">⭐</span>
                    <p><strong>@StartupQueen</strong> liked your domain</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* Feature 5: Leaderboards */}
        <section className="mb-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <Card className="p-8 order-2 lg:order-1">
              <h3 className="text-xl font-bold mb-6">🏆 Monthly Rankings</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-lg">
                  <span className="text-3xl">🥇</span>
                  <div className="flex-1">
                    <p className="font-bold">@DomainKing</p>
                    <p className="text-sm text-gray-600">$247K volume</p>
                  </div>
                  <Badge variant="warning">$500</Badge>
                </div>
                <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg">
                  <span className="text-3xl">🥈</span>
                  <div className="flex-1">
                    <p className="font-bold">@TechFlip</p>
                    <p className="text-sm text-gray-600">$189K volume</p>
                  </div>
                  <Badge>$250</Badge>
                </div>
                <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg">
                  <span className="text-3xl">🥉</span>
                  <div className="flex-1">
                    <p className="font-bold">@CryptoQueen</p>
                    <p className="text-sm text-gray-600">$156K volume</p>
                  </div>
                  <Badge variant="info">$100</Badge>
                </div>
              </div>
            </Card>
            <div className="order-1 lg:order-2">
              <Badge variant="success" className="mb-4">Gamification</Badge>
              <h2 className="text-4xl font-bold mb-6">Compete & Win Prizes</h2>
              <p className="text-lg text-gray-600 mb-6">
                Climb the leaderboards, earn achievements, and win real cash prizes
                every month. Top traders get exclusive badges and perks.
              </p>
              <Button onClick={() => navigate('/leaderboard')}>
                View Leaderboard
              </Button>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-20 bg-blue-600 text-white rounded-2xl text-center">
          <h2 className="text-4xl font-bold mb-4">Experience It Yourself</h2>
          <p className="text-xl mb-8">Try our interactive demo or start trading now</p>
          <div className="flex gap-4 justify-center">
            <Button size="lg" variant="secondary" onClick={() => navigate('/demo')}>
              Try Demo
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/pricing')}>
              See Pricing
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default FeaturesPage;
